import React from 'react';
import { X, Upload } from 'lucide-react';

interface Traveler {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  passport?: {
    number?: string;
    expiryDate?: string;
    file?: string;
  };
}

interface TravelerFormProps {
  travelers: Traveler[];
  onChange: (travelers: Traveler[]) => void;
}

export default function TravelerForm({ travelers, onChange }: TravelerFormProps) {
  const addTraveler = () => {
    onChange([...travelers, {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      passport: {
        number: '',
        expiryDate: '',
        file: ''
      }
    }]);
  };

  const removeTraveler = (index: number) => {
    const newTravelers = [...travelers];
    newTravelers.splice(index, 1);
    onChange(newTravelers);
  };

  const updateTraveler = (index: number, field: keyof Traveler, value: string) => {
    const newTravelers = [...travelers];
    newTravelers[index] = {
      ...newTravelers[index],
      [field]: value
    };
    onChange(newTravelers);
  };

  const updatePassport = (index: number, field: keyof Required<Traveler>['passport'], value: string) => {
    const newTravelers = [...travelers];
    newTravelers[index] = {
      ...newTravelers[index],
      passport: {
        ...newTravelers[index].passport,
        [field]: value
      }
    };
    onChange(newTravelers);
  };

  return (
    <div className="space-y-6">
      {travelers.map((traveler, index) => (
        <div key={index} className="relative bg-gray-50 p-6 rounded-lg">
          {index > 0 && (
            <button
              type="button"
              onClick={() => removeTraveler(index)}
              className="absolute top-4 right-4 text-gray-400 hover:text-red-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}

          <h4 className="text-lg font-medium mb-4">Voyageur {index + 1}</h4>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prénom
              </label>
              <input
                type="text"
                value={traveler.firstName}
                onChange={(e) => updateTraveler(index, 'firstName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom
              </label>
              <input
                type="text"
                value={traveler.lastName}
                onChange={(e) => updateTraveler(index, 'lastName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={traveler.email}
                onChange={(e) => updateTraveler(index, 'email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone
              </label>
              <input
                type="tel"
                value={traveler.phone}
                onChange={(e) => updateTraveler(index, 'phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          {/* Informations du passeport */}
          <div className="border-t pt-4">
            <h5 className="font-medium mb-4">Informations du passeport</h5>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Numéro de passeport
                </label>
                <input
                  type="text"
                  value={traveler.passport?.number || ''}
                  onChange={(e) => updatePassport(index, 'number', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date d'expiration
                </label>
                <input
                  type="date"
                  value={traveler.passport?.expiryDate || ''}
                  onChange={(e) => updatePassport(index, 'expiryDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addTraveler}
        className="w-full px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
      >
        Ajouter un voyageur
      </button>
    </div>
  );
}